import { useState, useEffect, useCallback } from 'preact/hooks';

/**
 * ImageLightbox — global full-screen image overlay.
 * Mounted per-shell (mobile/MobileShell.tsx AND desktop/DesktopShell.tsx — mirrors the
 * CoachLayer precedent), listens for the 'open-image-overlay' CustomEvent dispatched from
 * shared code. Replaces the per-component lightbox that was previously in ChatArea.
 */
export function ImageLightbox() {
  const [src, setSrc] = useState<string | null>(null);

  // Listen for the global event
  useEffect(() => {
    const handler = (e: Event) => {
      const imgSrc = (e as CustomEvent<string>).detail;
      if (imgSrc) {
        window.history.pushState({ lightbox: true }, '', '#lightbox');
        setSrc(imgSrc);
      }
    };
    window.addEventListener('open-image-overlay', handler);
    return () => window.removeEventListener('open-image-overlay', handler);
  }, []);

  // Browser back closes lightbox
  useEffect(() => {
    if (!src) return;
    const onPop = () => setSrc(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [src]);

  const close = useCallback(() => {
    if (window.history.state?.lightbox) window.history.back();
    else setSrc(null);
  }, []);

  const handleShare = useCallback(() => {
    if (!src) return;
    window.dispatchEvent(new CustomEvent('share-media', { detail: src }));
  }, [src]);

  if (!src) return null;

  return (
    <div class="image-overlay" onClick={close}>
      <button
        class="image-overlay-close"
        onClick={(e) => { e.stopPropagation(); close(); }}
        aria-label="Close image"
      >
        ✕
      </button>
      <img
        src={src}
        class="image-overlay-img"
        alt="full size"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        class="image-overlay-share"
        title="Share image"
        onClick={(e) => { e.stopPropagation(); handleShare(); }}
      >
        ↗ Share
      </button>
    </div>
  );
}
